import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { IllegalOperationException } from '../../exceptions/illegal-operation-exception.js';
import { cloneList, cloneListOrNull, isSameList } from '../dto/data-model-utils.js';

export abstract class BooleanOrList<T> implements ICloneable, IDataComparable {
  abstract get isBoolean(): boolean;
  abstract get isList(): boolean;

  abstract get booleanValue(): boolean;
  abstract get listValue(): T[];

  abstract encode(): boolean | T[];

  abstract clone(): this

  abstract isSame(other: this): boolean
}

export class BooleanOrListOfString extends BooleanOrList<string> {
  private _isBoolean: boolean;
  private _listValue: string[] | null;
  private _booleanValue: boolean | null;

  private constructor(
      isBoolean: boolean,
      booleanValue: boolean | null,
      listValue: string[] | null,
  ) {
    super();

    this._isBoolean = isBoolean;
    this._booleanValue = booleanValue;
    this._listValue = listValue;
  }

  get isBoolean(): boolean {
    return this._isBoolean;
  }

  get isList(): boolean {
    return !this._isBoolean;
  }

  get booleanValue(): boolean {
    if(this._booleanValue == null) {
      throw new IllegalOperationException('boolean value is not accessible on a list instance');
    }

    return this._booleanValue!;
  }

  get listValue(): string[] {
    if(this._listValue == null) {
      throw new IllegalOperationException('list value is not accessible on a boolean instance');
    }

    return this._listValue!;
  }

  static fromBoolean(value: boolean): BooleanOrListOfString {
    return new BooleanOrListOfString(true, value, null);
  }

  static fromList(value: string[]): BooleanOrListOfString {
    return new BooleanOrListOfString(false, null, cloneList<string>(value));
  }

  static decode(value: unknown): BooleanOrListOfString {
    if(value === false) {
      return new BooleanOrListOfString(true, false, null);
    }
    if(value === true) {
      return new BooleanOrListOfString(true, true, null);
    }

    return new BooleanOrListOfString(false, null, value as string[]);
  }

  static decodeOrNull(value: unknown): BooleanOrListOfString | null {
    return value != null ? this.decode(value) : null;
  }

  encode(): boolean | string[] {
    return this._isBoolean ? this._booleanValue! : this._listValue!;
  }

  clone(): this {
    return new BooleanOrListOfString(
        this._isBoolean,
        this._booleanValue,
        cloneListOrNull(this._listValue),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other._listValue, this._listValue) &&
        other._isBoolean == this._isBoolean &&
        other._booleanValue == this._booleanValue;
  }
}
